import { RRule } from 'rrule';
import { type } from 'arktype';
import { occurrencesBetween } from './occurrence';
import { createRule, DATE_KEY_PATTERN, DAY_IN_MILLISECONDS, dateKeyToUtc, earlierDate, laterDate, utcToDateKey } from './rrule-dates';

export interface ValidityWindow {
  start: string | null;
  end: string | null;
  rrule?: string | null;
}

export type ValidityTranslation = (key: string, params?: Record<string, unknown>) => string;

export const rruleSchema = type('string | null | undefined').narrow((value, ctx) => {
  if (value === null || value === undefined || validateRrule(value).valid) return true;
  return ctx.mustBe('a valid iCal RRULE body');
});

type ValidationResult = { valid: true } | { valid: false; error: string };

const SUMMARY_KEYS: Partial<Record<number, string>> = {
  [RRule.DAILY]: 'validity.summary.daily',
  [RRule.WEEKLY]: 'validity.summary.weekly',
  [RRule.MONTHLY]: 'validity.summary.monthly',
};

export function isValidOnDate(window: ValidityWindow, isoDate: string): boolean {
  if (!isWithinWindow(window, isoDate)) return false;
  if (!window.rrule) return true;
  if (!window.start) return false;

  try {
    return expandOnDate(createRule(window.rrule, window.start), isoDate);
  } catch {
    return false;
  }
}

export function isValidInRange(window: ValidityWindow, from: string | null, to: string | null): boolean {
  if (!isOrderedRange(from, to) || !overlapsWindow(window, from, to)) return false;
  if (!window.rrule) return true;
  if (!window.start) return false;

  try {
    const rule = createRule(window.rrule, window.start);
    const effectiveFrom = laterDate(window.start, from);
    const effectiveTo = earlierDate(window.end, to);
    return expandInRange(rule, effectiveFrom, effectiveTo);
  } catch {
    return false;
  }
}

export function validDatesInRange(window: ValidityWindow, from: string, to: string): Set<string> {
  return new Set(occurrencesBetween({ start: window.start, end: window.end, rrule: window.rrule ?? null }, from, to));
}

export function defaultRruleFromWindow(from: string, to: string): string {
  assertIsoDateKey(from);
  assertIsoDateKey(to);
  if (from > to) throw new RangeError(`Validity start must not be after end: ${from} > ${to}`);
  return `FREQ=DAILY;UNTIL=${to.replaceAll('-', '')}T000000Z`;
}

export function validateRrule(rrule: string): ValidationResult {
  const normalized = rrule.trim();
  if (!normalized) return { valid: false, error: 'RRULE is empty' };

  try {
    const parsed = RRule.fromString(`RRULE:${normalized}`);
    if (parsed.origOptions.freq === undefined) return { valid: false, error: 'RRULE is missing FREQ' };
    return { valid: true };
  } catch (error: unknown) {
    return { valid: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export function summarizeValidity(window: ValidityWindow, translate: ValidityTranslation, locale: string): string {
  const rule = window.rrule?.trim();
  if (!rule) return translate('validity.summary.always', { locale });

  try {
    const parsed = RRule.fromString(`RRULE:${rule}`);
    const key = SUMMARY_KEYS[parsed.options.freq] ?? 'validity.summary.custom';
    return translate(key, { locale, rrule: rule });
  } catch {
    return translate('validity.summary.custom', { locale, rrule: rule });
  }
}

export function fromValidFromTo(row: {
  validFrom: string | Date | null | undefined;
  validTo: string | Date | null | undefined;
  rrule?: string | null;
}): ValidityWindow {
  return { start: toDateKey(row.validFrom), end: toDateKey(row.validTo), rrule: row.rrule ?? null };
}

export function fromStartEnd(row: {
  startDate: string | Date | null | undefined;
  endDate: string | Date | null | undefined;
  rrule?: string | null;
}): ValidityWindow {
  return { start: toDateKey(row.startDate), end: toDateKey(row.endDate), rrule: row.rrule ?? null };
}

export function filterActiveOn<T extends { validFrom: string | Date | null; validTo: string | Date | null; rrule?: string | null }>(
  rows: readonly T[],
  date: string
): T[] {
  return rows.filter(row => isValidOnDate(fromValidFromTo(row), date));
}

// Date input is treated as a UTC instant; callers passing a local wall-clock Date near midnight risk a day shift.
function toDateKey(value: string | Date | null | undefined): string | null {
  if (value == null) return null;
  if (typeof value === 'string') return value;
  return Number.isNaN(value.getTime()) ? null : utcToDateKey(value);
}

export function assertIsoDateKey(value: string): void {
  if (!DATE_KEY_PATTERN.test(value)) throw new Error(`Invalid ISO date: ${value}`);
}

export function parseByDayWeekdays(rrule: string): number[] {
  try {
    const parsed = RRule.fromString(`RRULE:${rrule}`);
    const byweekday = parsed.options.byweekday;
    if (!Array.isArray(byweekday) || byweekday.length === 0) return [];
    return [...new Set(byweekday)].sort((a, b) => a - b);
  } catch {
    return [];
  }
}

function isWithinWindow(window: ValidityWindow, isoDate: string): boolean {
  return (window.start === null || window.start <= isoDate) && (window.end === null || isoDate <= window.end);
}

function overlapsWindow(window: ValidityWindow, from: string | null, to: string | null): boolean {
  if (window.start !== null && window.end !== null && window.start > window.end) return false;
  return (from === null || window.end === null || from <= window.end) && (to === null || window.start === null || window.start <= to);
}

function isOrderedRange(from: string | null, to: string | null): boolean {
  return from === null || to === null || from <= to;
}

function expandOnDate(rule: RRule, isoDate: string): boolean {
  const dayStart = dateKeyToUtc(isoDate);
  const dayEnd = new Date(dayStart.getTime() + DAY_IN_MILLISECONDS - 1);
  return rule.between(dayStart, dayEnd, true).some(occurrence => utcToDateKey(occurrence) === isoDate);
}

function expandInRange(rule: RRule, from: string | null, to: string | null): boolean {
  if (from === null) return false;
  const rangeStart = dateKeyToUtc(from);
  if (to === null) return rule.after(rangeStart, true) !== null;

  const rangeEnd = new Date(dateKeyToUtc(to).getTime() + DAY_IN_MILLISECONDS - 1);
  return rule.between(rangeStart, rangeEnd, true).length > 0;
}
